import React, { useEffect, useMemo, useCallback, useState } from 'react'
import { Typography, Card, Row, Col, Alert, Button, App, Skeleton } from 'antd'
import { PiWarningCircleBold, PiChartBarBold, PiLightningBold, PiArrowRight } from 'react-icons/pi'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { usePostHog } from 'posthog-js/react'
import dayjs from 'dayjs'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { PTOSuggestionsContextInfo, EnergyMode, EnergyCheckInType } from '@vacationtracker/shared/types/user-insight'
import { getEnergyModeConfig, getEnergyModeNameMap, submitEnergyCheckIn } from './services'
import { useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { useEnergyScore } from '../../hooks/use-energy-score'
import { FrontendUrls } from '../../types/urls'
import EnergyGauge from './components/energy-gauge'
import SuggestedActions from './components/suggested-actions'
import EnergyTrendChart from './components/energy-trend-chart'
import ScoreBreakdown from './components/score-breakdown'
import FeedbackInfo from './components/feedback-info'
import RechargeTypeSummaryCard from './components/recharge-type-summary-card'
import EnergyLevelSkeleton from './components/energy-level-skeleton'
import {
  useEnergyLevelData,
  usePTOSuggestions,
  useEnergyLevelTracking,
  useLeaveTypes
} from './hooks'

const { Title, Text } = Typography

interface EnergyLevelPageProps {
  referenceTimestamp?: string
}

const EnergyLevelPage: React.FC<EnergyLevelPageProps> = ({ referenceTimestamp }) => {
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()
  const history = useHistory()
  const posthog = usePostHog()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)

  // Check if energy score feature flag is enabled and get recharge details
  const { isFeatureEnabled: isEnergyScoreEnabled, rechargeDetails, isLoading, refetch: refetchEnergyScore } = useEnergyScore({
    currentUserId: authUser?.id,
    companyId: authCompany?.id,
  })

  // Initialize PostHog session recording
  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT === 'production') {
      posthog.startSessionRecording({
        sampling: true,
      })
    }
  }, [posthog])

  // Redirect to dashboard if feature flag is disabled
  useEffect(() => {
    if (!isEnergyScoreEnabled) {
      history.replace(FrontendUrls.dashboard)
    }
  }, [isEnergyScoreEnabled, history])

  // Fetch data using custom hooks
  const { energyData, loading, error, silentRefetch: silentRefetchEnergyData } = useEnergyLevelData({
    referenceTimestamp,
    isFeatureEnabled: isEnergyScoreEnabled,
  })

  const { ptoSuggestions, loading: ptoLoading, error: ptoError, silentRefetch: silentRefetchPTOSuggestions } = usePTOSuggestions({
    isFeatureEnabled: isEnergyScoreEnabled,
  })

  const { leaveTypes } = useLeaveTypes()

  // Tracking callbacks from custom hook
  const {
    handleSuggestedLeaveClick,
    handleTooltipView,
    handleActionTaken,
    handleFeedbackLinkClick,
    handleFeedbackSubmit,
    handleViewFullTeamCalendarClick,
  } = useEnergyLevelTracking({
    isFeatureEnabled: isEnergyScoreEnabled,
    energyData,
    ptoSuggestions,
    leaveTypes,
    loading,
    ptoLoading,
    error,
    ptoError,
  })

  // Handle feedback success - refetch energy data and PTO suggestions
  const handleFeedbackSuccess = async () => {
    await silentRefetchEnergyData()
    void silentRefetchPTOSuggestions()
  }

  // Handle recharge type quiz completion - refetch energy score and PTO suggestions data
  const handleRechargeTypeComplete = useCallback(() => {
    refetchEnergyScore()
    void silentRefetchPTOSuggestions()
  }, [refetchEnergyScore, silentRefetchPTOSuggestions])

  // Extract checkin state data from ptoSuggestions for special states
  const checkinStateData = useMemo(() => {
    if (!ptoSuggestions?.contextInfo) return null

    const { type, meta } = ptoSuggestions.contextInfo

    // Format vacation dates from the meta if available
    const formatVacationDates = () => {
      if (meta?.leaveStartDate && meta?.leaveEndDate) {
        const start = dayjs(meta.leaveStartDate).format('MMM D')
        const end = dayjs(meta.leaveEndDate).format('MMM D')
        return `${start} - ${end}`
      }
      return undefined
    }

    // Calculate recommendation resume date (e.g., 7 days after return)
    const getRecommendationResumeDate = () => {
      if (meta?.recommendationResumeDate) {
        return dayjs(meta.recommendationResumeDate).format('MMM D')
      }
      // Fallback: calculate from recentLeaveThresholdDays
      if (meta?.daysSinceReturn !== undefined && meta?.recentLeaveThresholdDays !== undefined) {
        const daysRemaining = meta.recentLeaveThresholdDays - meta.daysSinceReturn
        if (daysRemaining > 0) {
          return dayjs().add(daysRemaining, 'day').format('MMM D')
        }
      }
      return undefined
    }

    // Format balance reset date
    const getBalanceResetDate = () => {
      if (meta?.balanceResetDate) {
        return dayjs(meta.balanceResetDate).format('MMMM D, YYYY')
      }
      // Fallback: assume end of year
      return dayjs().endOf('year').format('MMMM D, YYYY')
    }

    // Extract latest energy checkin data
    const latestCheckIn = meta?.latestEnergyCheckIn

    return {
      contextType: type,
      vacationDates: formatVacationDates(),
      daysUntilLeave: meta?.daysUntilLeave,
      daysSinceReturn: meta?.daysSinceReturn,
      balanceResetDate: type === PTOSuggestionsContextInfo.INSUFFICIENT_BALANCE ? getBalanceResetDate() : undefined,
      nextRecommendationDate: type === PTOSuggestionsContextInfo.RECENT_RETURN ? getRecommendationResumeDate() : undefined,
      hasCheckinSubmitted: latestCheckIn?.submittedAt !== null,
      previousCheckinMode: latestCheckIn?.energyMode ?? undefined,
      checkinType: type === PTOSuggestionsContextInfo.UPCOMING_LEAVE
        ? EnergyCheckInType.PRE_VACATION
        : EnergyCheckInType.POST_VACATION,
    }
  }, [ptoSuggestions?.contextInfo])

  // Track local checkin state for immediate UI update after submission
  const [localCheckinState, setLocalCheckinState] = useState<{
    hasSubmitted: boolean
    selectedMode: EnergyMode | null
  } | null>(null)

  // Handler for energy checkin submission
  const handleCheckinEnergySelect = useCallback(async (mode: EnergyMode) => {
    if (!checkinStateData?.checkinType) return

    // Update local state immediately for responsive UI
    setLocalCheckinState({
      hasSubmitted: true,
      selectedMode: mode,
    })

    try {
      await submitEnergyCheckIn(checkinStateData.checkinType, mode)
      notification.success({
        message: formatMessage({ id: 'energyLevel.checkin.thankYou' }),
        duration: 3,
      })
    } catch (error) {
      console.error('Error submitting energy checkin:', error)
      // Reset local state on error
      setLocalCheckinState(null)
      notification.error({
        message: formatMessage({ id: 'energyLevel.checkin.error' }),
        duration: 5,
      })
    }
  }, [checkinStateData?.checkinType, notification, formatMessage])

  // Combine API data with local state for immediate UI updates
  const effectiveCheckinState = useMemo(() => {
    if (localCheckinState) {
      return {
        hasSubmitted: localCheckinState.hasSubmitted,
        selectedMode: localCheckinState.selectedMode,
      }
    }
    return {
      hasSubmitted: checkinStateData?.hasCheckinSubmitted || false,
      selectedMode: checkinStateData?.previousCheckinMode ?? null,
    }
  }, [localCheckinState, checkinStateData])

  if (loading || ptoLoading) {
    return <EnergyLevelSkeleton />
  }

  if (error || !energyData) {
    const isNoDataError = error === formatMessage({ id: 'energyLevel.noDataTitle' })

    return (
      <div className="energy-level-page">
        <div className="energy-page-content center-content">
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <div className={isNoDataError ? 'no-data-state' : 'error-state'}>
                  {isNoDataError ? (
                    <>
                      <div className="no-data-icon"><PiChartBarBold /></div>
                      <Title level={4} className="no-data-title">
                        <IntlMessages id="energyLevel.noDataTitle" />
                      </Title>
                      <Text type="secondary" className="no-data-message">
                        {error}
                      </Text>
                    </>
                  ) : (
                    <>
                      <PiWarningCircleBold className="error-icon" />
                      <Title level={4} className="error-title">
                        <IntlMessages id="energyLevel.errorTitle" />
                      </Title>
                      <Text type="secondary" className="error-message">
                        {error || formatMessage({ id: 'energyLevel.errorMessage' })}
                      </Text>
                    </>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    )
  }

  // Get energy mode config for styling the info banner and pass to child components
  const modeConfig = getEnergyModeConfig(energyData.currentScore.current, formatMessage)
  const energyModeNameMap = getEnergyModeNameMap(formatMessage)

  return (
    <div className="energy-level-page">
      <div className="energy-header-banner">
        <div className="energy-header-container center-content">
          <div className="energy-header-content">
            <Title level={2} className="energy-header-title">
              {modeConfig.topBannerMessage}
            </Title>
            <Text className="energy-header-subtitle">
              <IntlMessages id="energyLevel.dashboardSubtitle" />
            </Text>
          </div>
        </div>
      </div>

      <div className="energy-page-content center-content">
        {energyData.humanCenteredMessage && (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Alert
                message={energyData.humanCenteredMessage}
                type="info"
                icon={
                  <span>
                    <PiLightningBold style={{ color: modeConfig.primaryColor }} />
                  </span>
                }
                showIcon
                className={`energy-info-banner energy-info-banner--${energyData.energyMode}`}
              />
            </Col>
          </Row>
        )}
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card>
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={8}>
                  <EnergyGauge
                    score={energyData.currentScore.current}
                    mode={energyData.energyMode}
                    description={energyData.modeDescription}
                    currentEnergyModeConfig={modeConfig}
                    energyModeNameMap={energyModeNameMap}
                    onTooltipView={handleTooltipView}
                    onActionTaken={handleActionTaken}
                    onFeedbackLinkClick={handleFeedbackLinkClick}
                    onFeedbackSubmit={handleFeedbackSubmit}
                    onFeedbackSuccess={handleFeedbackSuccess}
                  />
                </Col>

                <Col xs={24} lg={16}>
                  <EnergyTrendChart
                    energyTrend={energyData.energyTrend}
                    departmentTrend={energyData.departmentTrend}
                    actionEffect={
                      ptoSuggestions?.suggestions.find(s => s.isBestSuggestion)
                        ? formatMessage(
                          { id: 'energyLevel.actionEffect' },
                          {
                            durationDays: ptoSuggestions.suggestions.find(s => s.isBestSuggestion)!.durationDays,
                            improvement: ptoSuggestions.suggestions.find(s => s.isBestSuggestion)!.energyPrediction.improvement,
                            improvementPercentage: ptoSuggestions.suggestions.find(s => s.isBestSuggestion)!.energyPrediction.improvementPercentage.toFixed(1),
                          }
                        )
                        : energyData.actionEffect
                    }
                    bestPTOSuggestion={ptoSuggestions?.suggestions.find(s => s.isBestSuggestion)}
                    trend={energyData.currentScore.trend}
                    feedbackInfo={energyData.feedbackInfo}
                    energyModeNameMap={energyModeNameMap}
                    onTooltipView={handleTooltipView}
                    onActionTaken={handleActionTaken}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="energy-level-bottom-section" style={{ marginTop: 16 }}>
          {/* Score Breakdown - appears between chart and suggestions at 1530px and below */}
          <Col xs={24} xl={8} className="score-breakdown-col score-breakdown-col-responsive">
            <div className="score-breakdown-wrapper">
              <Card className="score-breakdown-card">
                <ScoreBreakdown
                  scoreBreakdown={energyData.scoreBreakdown}
                  onTooltipView={handleTooltipView}
                  onActionTaken={handleActionTaken}
                />
                {energyData.feedbackInfo && (
                  <FeedbackInfo feedbackInfo={energyData.feedbackInfo} />
                )}
              </Card>

              {/* Recharge Type Summary Card - shown when user has recharge type */}
              {!isLoading && rechargeDetails && (
                <Card className="recharge-type-card">
                  <RechargeTypeSummaryCard
                    rechargeDetails={rechargeDetails}
                    userId={authUser?.id}
                    companyId={authCompany?.id}
                  />
                </Card>
              )}
            </div>
          </Col>

          {/* Suggested Actions */}
          <Col xs={24} xl={16} className="suggested-actions-col">
            <Card>
              {ptoLoading ? (
                <div style={{ padding: '16px 0' }}>
                  <Skeleton.Input active style={{ width: 180, marginBottom: 16 }} />
                  <Row gutter={[12, 12]}>
                    {[1, 2, 3].map((i) => (
                      <Col xs={24} sm={8} key={i}>
                        <Card size="small" style={{ borderRadius: 8 }}>
                          <Skeleton active paragraph={{ rows: 3 }} />
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : ptoError || !ptoSuggestions ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <PiWarningCircleBold style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                  <div>
                    <Text type="secondary">
                      {ptoError || formatMessage({ id: 'energyLevel.unableToLoadPTO' })}
                    </Text>
                  </div>
                </div>
              ) : (
                <>
                  <SuggestedActions
                    ptoSuggestions={ptoSuggestions}
                    daysSinceLastBreak={energyData.daysSinceLastBreak}
                    currentEnergyScore={energyData.currentScore.current}
                    userName={undefined} // Could be passed from user context if available
                    meta={energyData.meta}
                    rechargeType={rechargeDetails?.rechargeType}
                    onSuggestedLeaveClick={handleSuggestedLeaveClick}
                    onTooltipView={handleTooltipView}
                    onActionTaken={handleActionTaken}
                    onFeedbackLinkClick={handleFeedbackLinkClick}
                    // Checkin state props
                    balanceResetDate={checkinStateData?.balanceResetDate}
                    vacationDates={checkinStateData?.vacationDates}
                    daysUntilLeave={checkinStateData?.daysUntilLeave}
                    nextRecommendationDate={checkinStateData?.nextRecommendationDate}
                    previousCheckinMode={effectiveCheckinState.selectedMode}
                    onCheckinEnergySelect={handleCheckinEnergySelect}
                    onRechargeTypeComplete={handleRechargeTypeComplete}
                  />
                  <div className="calendar-footer">
                    <Button
                      type="link"
                      onClick={() => {
                        handleViewFullTeamCalendarClick()
                        // Use requestAnimationFrame to ensure tracking event fires before navigation
                        requestAnimationFrame(() => {
                          history.push('/app/calendar')
                        })
                      }}
                      icon={<PiArrowRight />}
                      iconPosition="end"
                    >
                      <IntlMessages id="energyLevel.viewFullCalendar" />
                    </Button>
                  </div>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default EnergyLevelPage
